import React from 'react';
import { Calendar, Clock, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета рабочего времени
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  setErrors,
  errors,
  isCalculating
}) => {
  // Массив месяцев для быстрого выбора
  const months = [
    { value: 0, name: 'Янв' },
    { value: 1, name: 'Фев' },
    { value: 2, name: 'Мар' },
    { value: 3, name: 'Апр' },
    { value: 4, name: 'Май' },
    { value: 5, name: 'Июн' },
    { value: 6, name: 'Июл' },
    { value: 7, name: 'Авг' },
    { value: 8, name: 'Сен' },
    { value: 9, name: 'Окт' },
    { value: 10, name: 'Ноя' },
    { value: 11, name: 'Дек' }
  ];
  
  // Обработчик изменения рабочих часов в день
  const handleWorkHoursPerDayChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      workHoursPerDay: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.workHoursPerDay) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.workHoursPerDay;
        return newErrors;
      });
    }
  };
  
  // Обработчик быстрого выбора месяца
  const handleQuickMonthSelect = (monthValue) => {
    if (formData.periodType !== 'month') {
      setFormData(prev => ({
        ...prev,
        periodType: 'month',
        month: monthValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        month: monthValue
      }));
    }
  };
  
  // Обработчик быстрого выбора типа графика
  const handleQuickScheduleTypeSelect = (scheduleType) => {
    setFormData(prev => {
      const newState = {
        ...prev,
        workScheduleType: scheduleType
      };
      
      // Устанавливаем стандартные значения для выбранного типа графика
      if (scheduleType === 'standard') {
        newState.workDaysPerWeek = 5;
        newState.workHoursPerDay = 8;
        newState.shiftPattern = '5/2';
      } else if (scheduleType === 'shift') {
        // Оставляем текущие значения или устанавливаем дефолтные
      }
      
      return newState;
    });
  };
  
  // Функция расчета
  const calculateHours = () => {
    // Вызываем функцию основного расчета в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-cyan-500" />
        Быстрая настройка
      </h3>
      
      <div className="space-y-5">
        {/* Быстрый выбор месяца */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Месяц расчета</label>
          </div>
          <div className="grid grid-cols-4 gap-1">
            {months.map((month, index) => {
              // Показываем только текущие 4 месяца в году
              if (index >= new Date().getMonth() - 1 && index <= new Date().getMonth() + 2) {
                return (
                  <button
                    key={month.value}
                    type="button"
                    onClick={() => handleQuickMonthSelect(month.value)}
                    className={`p-2 text-xs rounded border transition-colors
                      ${formData.periodType === 'month' && formData.month === month.value 
                        ? 'bg-cyan-100 border-cyan-300 text-cyan-700 dark:bg-cyan-900/30 dark:border-cyan-700 dark:text-cyan-300'
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                  >
                    {month.name}
                  </button>
                );
              }
              return null;
            })}
          </div>
        </div>
        
        {/* Быстрый выбор типа графика */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Тип графика</label>
          </div>
          <div className="grid grid-cols-2 gap-1">
            <button
              type="button"
              onClick={() => handleQuickScheduleTypeSelect('standard')}
              className={`p-2 text-xs rounded border transition-colors
                ${formData.workScheduleType === 'standard' 
                  ? 'bg-cyan-100 border-cyan-300 text-cyan-700 dark:bg-cyan-900/30 dark:border-cyan-700 dark:text-cyan-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Стандартный (5/2)
            </button>
            <button
              type="button"
              onClick={() => handleQuickScheduleTypeSelect('shift')}
              className={`p-2 text-xs rounded border transition-colors
                ${formData.workScheduleType === 'shift' 
                  ? 'bg-cyan-100 border-cyan-300 text-cyan-700 dark:bg-cyan-900/30 dark:border-cyan-700 dark:text-cyan-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Сменный график
            </button>
          </div>
        </div>
        
        {/* Рабочие часы в день */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="quickWorkHours" className="text-gray-600 dark:text-gray-400">
              Рабочих часов в день
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.workHoursPerDay} ч.
            </span>
          </div>
          <input
            type="range"
            id="quickWorkHours"
            min="1"
            max="12"
            step="0.5"
            value={formData.workHoursPerDay}
            onChange={handleWorkHoursPerDayChange}
            aria-label="Рабочих часов в день"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-cyan-600"
          />
          
          {/* Быстрые варианты часов */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[4, 6, 8, 12].map(hours => (
              <button
                key={hours}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, workHoursPerDay: hours }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${parseFloat(formData.workHoursPerDay) === hours 
                    ? 'bg-cyan-100 border-cyan-300 text-cyan-700 dark:bg-cyan-900/30 dark:border-cyan-700 dark:text-cyan-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {hours} ч.
              </button>
            ))}
          </div>
        </div>
        
        {/* Включение/выключение праздников */}
        <div>
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
              <Calendar className="w-4 h-4 mr-1.5 text-cyan-500" />
              Учитывать праздники
            </span>
            <div className="relative">
              <input 
                type="checkbox" 
                name="includeHolidays"
                checked={formData.includeHolidays}
                onChange={(e) => setFormData(prev => ({ ...prev, includeHolidays: e.target.checked }))}
                className="sr-only" 
              />
              <div className={`block w-10 h-6 rounded-full transition-colors ${formData.includeHolidays ? 'bg-cyan-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
              <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.includeHolidays ? 'transform translate-x-4' : ''}`}></div>
            </div>
          </label>
        </div>
        
        {/* Включение/выключение ночных смен */}
        <div>
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
              <Clock className="w-4 h-4 mr-1.5 text-cyan-500" />
              Учитывать ночные часы
            </span>
            <div className="relative">
              <input 
                type="checkbox" 
                name="nightShifts"
                checked={formData.nightShifts}
                onChange={(e) => setFormData(prev => ({ ...prev, nightShifts: e.target.checked }))}
                className="sr-only" 
              />
              <div className={`block w-10 h-6 rounded-full transition-colors ${formData.nightShifts ? 'bg-cyan-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
              <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.nightShifts ? 'transform translate-x-4' : ''}`}></div>
            </div>
          </label>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateHours}
          icon={<Calculator className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать время
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;